// The notice shown when a message could not get a fresh plan and was not sent.
//
// Read-on-send used to wait 4s and then send on whatever copy of the plan the
// backend held, flagging it stale only in a field nobody saw. Now the message
// waits for the connector's read (see awaitFreshPlan) and, when there is none,
// is held back with this notice: why, and the two ways forward -- try again, or
// send on the older plan, labelled with when it was read.
//
// Kept out of taskpane.js (which cannot be imported in tests) so the wording and
// the button behaviour are unit-tested.

/** How long ago, in words, for "last heard from N minutes ago". */
function ago(seconds) {
  if (!Number.isFinite(seconds)) return null;
  if (seconds < 90) return `${Math.max(1, Math.round(seconds))} seconds ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours} hours ago` : `${Math.round(hours / 24)} days ago`;
}

/**
 * @param {{reason: string, connectorSeenSecondsAgo?: number|null,
 *          waitedMs?: number|null}} info
 * @returns {string}
 */
export function stalePlanNoticeText({
  reason,
  connectorSeenSecondsAgo = null,
  waitedMs = null,
}) {
  if (reason === "offline") {
    const heard = ago(connectorSeenSecondsAgo);
    const when = heard
      ? `Microsoft Project isn't connected (last heard from ${heard}),`
      : "Microsoft Project hasn't connected to Novatime yet,";
    return `Your message wasn't sent. ${when} so I can't read your current plan. ` +
      "Check that your plan is open in Project with Novatime AI running, then try again.";
  }
  if (reason === "timeout") {
    // The wait is the server's TTL, not a constant in this file, so the number
    // has to come from the attempt rather than be written here -- it said "20
    // seconds" while the pane's own deadline had moved.
    const secs = Number.isFinite(waitedMs) && waitedMs > 0
      ? Math.round(waitedMs / 1000)
      : null;
    const within = secs ? `within ${secs} seconds` : "in time";
    // "It may be busy, for example with a dialog open" is kept because it is
    // accurate: a modal dialog makes Project's object model unavailable, the
    // connector then cannot read the plan, and that is the measured cause --
    // including this add-in's own update popup. An earlier version of this
    // file blamed plan size instead, which the timings did not support: a
    // 1,013-task plan reads in 1.7s, and a 145-row plan hit this same failure.
    return `Your message wasn't sent. Microsoft Project didn't send your current plan ${within}. ` +
      "It may be busy, for example with a dialog open or a sync running. Try again in a moment.";
  }
  return "Your message wasn't sent. I couldn't reach Novatime to read your current plan. " +
    "Check your connection and try again.";
}

/**
 * Label for sending on the plan the backend already holds.
 * @param {string|null} planReadAt  ISO time the held plan was read
 * @param {Date} [now]
 */
export function sendAnywayLabel(planReadAt, now = new Date()) {
  const read = planReadAt ? new Date(planReadAt) : null;
  if (!read || Number.isNaN(read.getTime())) return "Send without a current plan";
  const time = read.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const sameDay = read.toDateString() === now.toDateString();
  const day = sameDay ? "" : `${read.toLocaleDateString([], { month: "short", day: "numeric" })}, `;
  return `Send with plan from ${day}${time}`;
}

/**
 * @param {Document} doc
 * @param {{reason: string, connectorSeenSecondsAgo?: number|null, planReadAt?: string|null,
 *          onRetry: () => void, onSendAnyway: () => void}} options
 * @returns {HTMLElement} a `.message.system-message` element, not yet attached
 */
export function buildStalePlanNotice(doc, {
  reason,
  connectorSeenSecondsAgo = null,
  planReadAt = null,
  waitedMs = null,
  onRetry,
  onSendAnyway,
}) {
  const message = doc.createElement("div");
  message.className = "message system-message stale-plan-notice";

  const content = doc.createElement("div");
  content.className = "message-content";
  const text = doc.createElement("div");
  text.className = "message-text";
  text.textContent = stalePlanNoticeText({ reason, connectorSeenSecondsAgo, waitedMs });
  content.appendChild(text);

  const actions = doc.createElement("div");
  actions.className = "stale-plan-actions";
  const retry = doc.createElement("button");
  retry.type = "button";
  retry.className = "stale-plan-button stale-plan-retry";
  retry.textContent = "Try again";
  const anyway = doc.createElement("button");
  anyway.type = "button";
  anyway.className = "stale-plan-button stale-plan-send-anyway";
  anyway.textContent = sendAnywayLabel(planReadAt);

  // One choice per notice: a second click (or the other button) would send twice.
  let chosen = false;
  const choose = (handler) => () => {
    if (chosen) return;
    chosen = true;
    retry.disabled = true;
    anyway.disabled = true;
    handler();
  };
  retry.addEventListener("click", choose(onRetry));
  anyway.addEventListener("click", choose(onSendAnyway));

  actions.appendChild(retry);
  actions.appendChild(anyway);
  content.appendChild(actions);
  message.appendChild(content);
  return message;
}
